import React, { useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import emailValidator from 'email-validator'

import Modal from 'components/Shared/Modal'
import Icon from 'components/Shared/Icon'
import WrappedInput from 'components/Shared/Forms/WrappedInput'
import WrappedSelect from 'components/Shared/Forms/WrappedSelect'
import UpgradeAlertModal from 'components/Shared/UpgradeAlertModal'
import SeatAddonModal from 'components/Settings/Team/SeatAddonModal'

import { createInvite, fetchOrganization } from 'ducks/organizations'
import { updateCurrentUser } from 'ducks/users'
import { isCurrentPlanType } from 'utils/billing.ts'

import { INVITE_LIMIT_MESSAGE } from '../../../constants'

const TeamInviteModal = ({ organization, currentUser, onClose, onSuccess }) => {
  const dispatch = useDispatch()

  const [email, setEmail] = useState('')
  const [freelancer, setFreelancer] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showSeatLimitModal, setShowSeatLimitModal] = useState(false)
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false)

  const showFreelancerDropdown = currentUser?.persona !== 'Freelancer/Agency'

  const isValidEmail = useMemo(() => {
    return email && emailValidator.validate(email)
  }, [email])

  const disableButton = useMemo(() => {
    if (!isValidEmail) {
      return true
    }

    if (showFreelancerDropdown && freelancer === null && !currentUser?.admin) {
      return true
    }

    return false
  }, [isValidEmail, freelancer, currentUser, showFreelancerDropdown])

  const inviteUser = async emailToInvite => {
    try {
      await dispatch(
        createInvite({
          email: emailToInvite,
          organizationId: organization.id,
        })
      )

      await dispatch(fetchOrganization(organization.id))

      if (onSuccess) {
        onSuccess()
      }

      return true
    } catch (err) {
      const status = err.response?.status
      const data = err.response?.data

      let message = data?.error || 'An unknown error occurred'

      if (status === 429) {
        message = INVITE_LIMIT_MESSAGE
      }

      setError(message)

      return false
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const normalizedEmail = email.toLowerCase()

    // Check if user is already a member
    const members = organization.Users || []
    const existingMember = members.find(u => u.email === normalizedEmail)

    if (existingMember) {
      setError(`${normalizedEmail} is already part of your team!`)
      setLoading(false)

      return
    }

    const { addons, planType } = organization
    const seatLimit = addons?.seats?.limit
    const seatUsage = addons?.seats?.count

    if (planType === 'free') {
      setShowUpgradePlanModal(true)
      setLoading(false)

      return
    }

    if (isCurrentPlanType(planType) && seatUsage >= seatLimit) {
      setShowSeatLimitModal(true)
      setLoading(false)

      return
    }

    try {
      const success = await inviteUser(normalizedEmail)

      if (success && currentUser && freelancer) {
        dispatch(updateCurrentUser(currentUser.id, { client: 'Yes' }))
      }
    } catch (err) {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSeatAddonSuccess = async () => {
    setShowSeatLimitModal(false)
    const success = await inviteUser(email.toLowerCase())
    if (success && currentUser && freelancer) {
      dispatch(updateCurrentUser(currentUser.id, { client: 'Yes' }))
    }
  }

  return (
    <>
      <SeatAddonModal
        show={showSeatLimitModal}
        onClose={() => setShowSeatLimitModal(false)}
        callback={handleSeatAddonSuccess}
      />

      <UpgradeAlertModal
        action="Add Editors"
        body="Switch to a paid plan to start collaborating on your app design."
        onClose={() => setShowUpgradePlanModal(false)}
        show={showUpgradePlanModal}
      />

      <Modal onClose={onClose}>
        <form onSubmit={handleSubmit}>
          <Modal.Header>
            <h1>
              <div>
                Invite New Team Member
                <Icon type="party-guy" />
              </div>
              <span>This person will be invited only to your team.</span>
            </h1>
          </Modal.Header>
          <Modal.Content>
            <WrappedInput
              label="Email Address"
              autoComplete="off"
              placeholder="elon.musk@spacex.com"
              input={{
                value: email,
                onChange: e => setEmail(e.target.value),
              }}
              meta={{
                touched: !!error,
                error,
              }}
            />
            {showFreelancerDropdown && (
              <div className="wrapped-input">
                <label>Is this person a freelancer or from an agency?</label>
                <WrappedSelect
                  options={[
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' },
                  ]}
                  input={{
                    value: freelancer,
                    onChange: value => setFreelancer(value),
                  }}
                />
              </div>
            )}
          </Modal.Content>
          <Modal.Actions
            leftButtons={[
              <Modal.Button key="cancel" type="button" onClick={onClose} text>
                Cancel
              </Modal.Button>,
            ]}
          >
            <Modal.Button
              type="submit"
              teal
              loading={loading}
              disabled={disableButton}
            >
              Send Invite
            </Modal.Button>
          </Modal.Actions>
        </form>
      </Modal>
    </>
  )
}

export default TeamInviteModal
